import React, { useState } from 'react'
import surveyIllustration from '@/assets/images/survey_illustration.svg'

interface WelcomeScreenProps {
  surveyName: string
  onStart: (disabilityInfo: { hasDisability: boolean; disabilityType: string }) => void
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ surveyName, onStart }) => {
  const [hasDisability, setHasDisability] = useState<boolean | null>(null)
  const [disabilityType, setDisabilityType] = useState('')

  const canStart = hasDisability === false || (hasDisability === true && disabilityType !== '')

  const handleStart = () => {
    if (canStart) {
      onStart({ hasDisability: hasDisability!, disabilityType })
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        {/* Columna de ilustración - Oculta en móvil */}
        <div className="hidden md:block text-center md:text-left">
          <img src={surveyIllustration} alt="" className="w-full max-w-sm mx-auto" />
        </div>

        {/* Columna de contenido */}
        <div>
          <h1 className="text-2xl font-bold mb-4">Encuesta de satisfacción</h1>
          <p className="text-gray-800 mb-6">
            Queremos que los servicios digitales sean más accesibles y satisfactorios para toda la ciudadanía, por eso
            te invitamos a responder la siguiente encuesta.
          </p>

          {/* Tiempo estimado - visible en móvil */}
          <div className="mb-6">
            <div className="inline-flex items-center bg-blue-50 text-blue-700 rounded-full px-3 py-1 text-sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Menos de 1 min</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-col">
              <p className="font-semibold">Antes de iniciar selecciona una opción:</p>
              <p className="text-sm text-gray-800">Esto nos ayudará a personalizar las preguntas</p>
            </div>

            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="disability"
                className="form-radio h-5 w-5"
                onChange={() => setHasDisability(true)}
              />
              <span className="ml-3">Soy una persona con discapacidad</span>
            </label>

            {hasDisability && (
              <div className="md:pl-8">
                <label htmlFor="disability-type-select" className="block text-sm font-medium text-gray-700 mb-2">
                  Selecciona un tipo de discapacidad
                </label>
                <select
                  id="disability-type-select"
                  value={disabilityType}
                  onChange={e => setDisabilityType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg bg-white"
                  autoComplete="off"
                >
                  <option value="">Seleccionar...</option>
                  <option value="visual">Discapacidad visual</option>
                  <option value="hearing">Discapacidad auditiva</option>
                  <option value="motor">Discapacidad motora</option>
                  <option value="cognitive">Discapacidad cognitiva</option>
                </select>
              </div>
            )}

            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="disability"
                className="form-radio h-5 w-5"
                onChange={() => setHasDisability(false)}
              />
              <span className="ml-3">Soy una persona sin discapacidad</span>
            </label>
          </div>

          <div className="flex flex-col md:flex-row md:justify-end gap-4 mt-8">
            <button
              onClick={handleStart}
              disabled={!canStart}
              aria-disabled={!canStart}
              className="w-full md:w-auto px-6 py-3 md:py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-400"
            >
              Empezar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WelcomeScreen
